import {
  readMembershipProfileValue,
  toStoredMembershipProfileValue,
} from '@citizen-walk/shared-types';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { useCallback, useEffect, useRef, useState } from 'react';
import { db } from '../../lib/firebase';
import { EMPTY_USER_FIELDS_VALUES, type UserFieldsFormValues } from './UserFieldsForm';

/**
 * `participants/{uid}/memberships/{districtId}` の builtin column + profileFieldValues map を
 * UserFieldsFormValues 形式で読み書きする hook (Sprint BG-DataSettings 4)。
 *
 * 旧 v1 では `participants/{uid}/memberships/{districtId}` を直接編集していた。v2 でも同 path
 * (docs/04 §4)、本 hook が rules pass + merge 保存を担当。
 *
 * 注: rules で書込は本人 only (`request.auth.uid == uid`)。
 */
export interface UseMembershipUserFieldsReturn {
  status: 'loading' | 'ready' | 'error';
  values: UserFieldsFormValues;
  /**
   * membership doc の `joinedSessionIds` (参加セッション)。SettingsModal が draft の初期値に使う。
   * 参照は読込完了まで `[]`、読込後は doc の値で安定する (毎 render 新規生成しない)。
   */
  joinedSessionIds: string[];
  /** membership.mainSessionId (案1、投稿メインセッション)。SettingsModal が draft 初期値に使う。 */
  mainSessionId: string | null;
  error: Error | null;
  /**
   * membership を保存する。
   * @param next builtin column + profileFieldValues (UserFieldsFormValues)。
   * @param joinedSessionIds 渡された場合のみ `joinedSessionIds` を payload に含める
   *   (SettingsModal の参加セッション保存統合用。未指定なら従来どおりセッションは触らない)。
   * @param mainSessionId 渡された場合のみ `mainSessionId` を payload に含める (案1 メインセッション保存)。
   */
  save: (
    next: UserFieldsFormValues,
    joinedSessionIds?: string[],
    mainSessionId?: string | null,
  ) => Promise<void>;
}

export function useMembershipUserFields(
  uid: string | null,
  districtId: string | null,
): UseMembershipUserFieldsReturn {
  const [values, setValues] = useState<UserFieldsFormValues>(EMPTY_USER_FIELDS_VALUES);
  const [joinedSessionIds, setJoinedSessionIds] = useState<string[]>([]);
  const [mainSessionId, setMainSessionId] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<Error | null>(null);
  // 直前に反映した内容 (同じ内容の snapshot では state を作り直さない。lastSeenAt だけの更新等で
  // 参照が変わると、設定画面の下書きや依存する memo が無駄に作り直されるため)。
  const lastContent = useRef<string | null>(null);

  // 2026-10-09: 1 回読みから購読に変更。同じ人のほかの端末で変えた設定が、サーバー (onMembershipWrite)
  // 経由でこの端末の doc に届いたら、開き直さなくても反映する。
  useEffect(() => {
    lastContent.current = null;
    if (!uid || !districtId) {
      setStatus('ready');
      setValues(EMPTY_USER_FIELDS_VALUES);
      setJoinedSessionIds([]);
      setMainSessionId(null);
      return;
    }
    setStatus('loading');

    const ref = doc(db, 'participants', uid, 'memberships', districtId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        const nextJoined =
          data && Array.isArray(data.joinedSessionIds)
            ? (data.joinedSessionIds as unknown[]).filter((x): x is string => typeof x === 'string')
            : [];
        const nextMain =
          data && typeof data.mainSessionId === 'string' && data.mainSessionId.length > 0
            ? data.mainSessionId
            : null;
        const nextValues: UserFieldsFormValues = data
          ? {
              builtin: {
                nickname: typeof data.nickname === 'string' ? data.nickname : '',
                // 専用欄は項目の型に応じて文字列 / 配列 (複数選択) / 数値 / 真偽値。未回答は ''。
                gender: readMembershipProfileValue(data.gender) ?? '',
                ageGroup: readMembershipProfileValue(data.ageGroup) ?? '',
                relationship: readMembershipProfileValue(data.relationship) ?? '',
                attribute: readMembershipProfileValue(data.attribute) ?? '',
              },
              custom: (data.profileFieldValues as Record<string, unknown> | undefined) ?? {},
            }
          : EMPTY_USER_FIELDS_VALUES;
        const content = JSON.stringify([nextValues, nextJoined, nextMain]);
        if (content !== lastContent.current) {
          lastContent.current = content;
          setValues(nextValues);
          setJoinedSessionIds(nextJoined);
          setMainSessionId(nextMain);
        }
        setError(null);
        setStatus('ready');
      },
      (e) => {
        setError(e);
        setStatus('error');
      },
    );
    return () => unsub();
  }, [uid, districtId]);

  const save = useCallback(
    async (
      next: UserFieldsFormValues,
      nextJoinedSessionIds?: string[],
      nextMainSessionId?: string | null,
    ) => {
      if (!uid || !districtId) return;
      const ref = doc(db, 'participants', uid, 'memberships', districtId);
      // old2 入口フロー移植 Phase D 踏襲: 任意属性 (gender/ageGroup/relationship/attribute) は
      // 空文字を `null` 正規化して保存する (旧 SettingsModal.tsx `value || null`)。nickname は trim 済。
      const payload: Record<string, unknown> = {
        nickname: next.builtin.nickname.trim(),
        gender: toStoredMembershipProfileValue(next.builtin.gender),
        ageGroup: toStoredMembershipProfileValue(next.builtin.ageGroup),
        relationship: toStoredMembershipProfileValue(next.builtin.relationship),
        attribute: toStoredMembershipProfileValue(next.builtin.attribute),
        profileFieldValues: next.custom,
        lastSeenAt: serverTimestamp(),
      };
      // 参加セッションは引数が渡されたときのみ更新 (SettingsModal の保存ボタン統合)。
      // 未指定なら merge:true でセッションフィールドは温存する (後方互換)。
      if (nextJoinedSessionIds !== undefined) {
        payload.joinedSessionIds = nextJoinedSessionIds;
      }
      // 案1: メインセッションは引数が渡されたときのみ更新 (null も明示書込 = メイン解除)。
      if (nextMainSessionId !== undefined) {
        payload.mainSessionId = nextMainSessionId;
      }
      await setDoc(ref, payload, { merge: true });
      setValues(next);
      if (nextJoinedSessionIds !== undefined) {
        setJoinedSessionIds(nextJoinedSessionIds);
      }
      if (nextMainSessionId !== undefined) {
        setMainSessionId(nextMainSessionId);
      }
    },
    [uid, districtId],
  );

  return { status, values, joinedSessionIds, mainSessionId, error, save };
}
